import {
  BinIcon,
  Button,
  Menu,
  Modal,
  PlusIcon,
  Skeleton,
  Tooltip,
  Typography,
} from "@codat/orchard-ui";
import { useQueryClient } from "@tanstack/react-query";
import { Thread } from "api/schema/insights/threads";
import { useDeleteThread } from "hooks/query/useDeleteThread";
import { spendInsightsThreadsKeys } from "hooks/query/useGetThreads";
import React, { useCallback, useRef, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { formatDateAndTimeWithShortString } from "utils/formatDate";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";

import styles from "./Sidebar.module.scss";

interface GroupedThreads {
  label: string;
  items: Thread[];
}

function daysAgo(today: Date, days: number) {
  const date = new Date(today);
  date.setDate(date.getDate() - days);
  return date;
}

const MONTHS_IN_A_YEAR = 12;

function groupThreadsByDate(threads: Thread[]): GroupedThreads[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = daysAgo(today, 1);
  const sevenDaysAgo = daysAgo(today, 7);
  const thirtyDaysAgo = daysAgo(today, 30);
  const lastMonth = new Date(today);
  lastMonth.setMonth(today.getMonth() - 1);

  const groups: Record<string, Thread[]> = {};

  for (const thread of threads) {
    const createdAt = new Date(thread.createdAt);

    let key;
    if (createdAt >= today) key = "Today";
    else if (createdAt >= yesterday) key = "Yesterday";
    else if (createdAt >= sevenDaysAgo) key = "Previous 7 Days";
    else if (createdAt >= thirtyDaysAgo) key = "Previous 30 Days";
    else {
      const monthDiff =
        (now.getFullYear() - createdAt.getFullYear()) * MONTHS_IN_A_YEAR +
        (now.getMonth() - createdAt.getMonth());
      // If the thread is from the current year, use the month name
      if (monthDiff >= 1 && createdAt.getFullYear() === now.getFullYear()) {
        key = createdAt.toLocaleString("en-US", { month: "long" }); // Individual month name
      } else {
        key = createdAt.getFullYear().toString();
      }
    }

    if (!groups[key]) groups[key] = [];
    groups[key].push(thread);
  }

  // Custom sort order for categories
  const customSortOrder = [
    "Today",
    "Yesterday",
    "Previous 7 Days",
    "Previous 30 Days",
  ];

  // Get month names as strings
  const monthNames = Array.from({ length: MONTHS_IN_A_YEAR }, (_, i) =>
    new Date(0, i).toLocaleString("en-US", { month: "long" })
  );

  const sortedGroups = Object.entries(groups)
    .sort(([a], [b]) => {
      const indexA = customSortOrder.indexOf(a);
      const indexB = customSortOrder.indexOf(b);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB; // Keep predefined order
      if (indexA !== -1) return -1; // Custom order first
      if (indexB !== -1) return 1; // Custom order first

      const monthAIndex = monthNames.indexOf(a);
      const monthBIndex = monthNames.indexOf(b);

      const isMonthA = monthAIndex !== -1;
      const isMonthB = monthBIndex !== -1;

      if (isMonthA && isMonthB) {
        return monthBIndex - monthAIndex; // Sort months with most recent first
      }

      if (isMonthA) return -1; // Ensure months come before years
      if (isMonthB) return 1;

      // If both are years, sort descending (2024 before 2023)
      const yearA = parseInt(a, 10);
      const yearB = parseInt(b, 10);
      if (!isNaN(yearA) && !isNaN(yearB)) return yearB - yearA;

      return 0;
    })
    .map(([label, items]) => ({ label, items }));

  return sortedGroups;
}

interface MenuSubListProps {
  group: GroupedThreads;
  onDeleteThread: (threadId: string) => void;
}

const MenuSubList = ({ group, onDeleteThread }: MenuSubListProps) => {
  const { companyId } = useParams<{
    companyId: string;
  }>();

  const location = useLocation();
  const selectedThread = location.pathname.match(/\/threads\/([^/]+)/);
  const selectedThreadId = selectedThread ? selectedThread[1] : undefined;

  return (
    <div>
      <Typography
        as="div"
        variant="heading_xs"
        className={styles.threadsSubHeading}
        data-testid="threads-group-heading"
        noMargin
      >
        {group.label}
      </Typography>
      <Menu.List className={styles.threadsList}>
        {group.items.map((item) => {
          return (
            <Menu.Item
              onClick={undefined}
              selected={item.threadId === selectedThreadId}
              key={item.threadId}
              className={styles.threadsListItem}
            >
              <Link
                to={formatUrl(paths.spendInsights.company.THREAD, {
                  companyId,
                  threadId: item.threadId,
                })}
              >
                {`${formatDateAndTimeWithShortString(item.createdAt, true)}`}
              </Link>
              <Button
                variant="tertiary"
                size="small"
                color="interface"
                data-testid="delete-thread-button"
                label="Delete conversation"
                onClick={() => onDeleteThread(item.threadId)}
              >
                <BinIcon />
              </Button>
            </Menu.Item>
          );
        })}
      </Menu.List>
    </div>
  );
};

const GroupedThreads = ({
  groupedThreads,
  onDeleteThread,
}: {
  groupedThreads: GroupedThreads[];
  onDeleteThread: (threadId: string) => void;
}) => (
  <div>
    {groupedThreads.map((group) => (
      <MenuSubList
        key={group.label}
        group={group}
        onDeleteThread={onDeleteThread}
      />
    ))}
  </div>
);

export interface SidebarProps {
  isLoadingThreads: boolean;
  threads: Thread[] | undefined;
  onCreateThread: () => void;
  isCreatingThread: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingThreads: boolean;
}

const Sidebar = ({
  isLoadingThreads,
  threads,
  onCreateThread,
  isCreatingThread,
  fetchNextPage,
  hasNextPage = true,
  isFetchingThreads,
}: SidebarProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [threadIdToDelete, setThreadIdToDelete] = useState<string | null>(null);
  const observer = useRef<IntersectionObserver>();
  const { companyId } = useParams<{ companyId: string }>();
  const newThreadButtonDisabled = isCreatingThread || isLoadingThreads;
  const history = useHistory();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteThread, isPending } = useDeleteThread({
    companyId,
  });

  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoadingThreads) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(([firstEntry]) => {
        if (firstEntry.isIntersecting && hasNextPage && !isFetchingThreads) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetchingThreads, isLoadingThreads]
  );

  const cancelDelete = () => setThreadIdToDelete(null);

  const onDeleteThread = async () => {
    if (!threadIdToDelete) return;
    await deleteThread({ threadId: threadIdToDelete });
    setThreadIdToDelete(null);
    await queryClient.refetchQueries({
      queryKey: spendInsightsThreadsKeys.threadsForCompany({
        companyId,
      }),
    });
    history.push(
      formatUrl(paths.spendInsights.company.THREAD, {
        threadId: threads?.[0].threadId,
        companyId,
      })
    );
  };

  return (
    <>
      {threadIdToDelete && (
        <Modal
          size="small"
          onClose={cancelDelete}
          data-testid="delete-thread-modal"
        >
          <div>
            <Modal.Header
              title="Delete this conversation?"
              onClose={cancelDelete}
            />
            <Modal.Body variant="light">
              <Modal.Section variant="fill">
                <Typography as="p" variant="body_md" noMargin>
                  This action cannot be undone.
                </Typography>
              </Modal.Section>
            </Modal.Body>
            <Modal.Footer
              variant="dark"
              buttons={[
                {
                  label: "Cancel",
                  color: "action",
                  variant: "tertiary",
                  size: "small",
                  onClick: cancelDelete,
                },
                {
                  label: "Delete",
                  size: "small",
                  isLoading: isPending,
                  onClick: onDeleteThread,
                  ["data-testid"]: "confirm-delete-thread-btn",
                },
              ]}
            />
          </div>
        </Modal>
      )}
      <aside data-testid="sidebar" className={styles.sidebar}>
        <div className={styles.headingWrapper}>
          <Typography
            as="h2"
            variant="heading_sm"
            noMargin
            data-testid="menu-title"
          >
            Conversations
          </Typography>
          <Tooltip
            isOpen={isTooltipOpen}
            placement="top"
            className={styles.tooltipContent}
            content={
              <Typography as="span" variant="body_md" noMargin>
                New conversation
              </Typography>
            }
          >
            <Button
              onClick={onCreateThread}
              variant="primary"
              color="action"
              size="small"
              className={styles.newThreadButton}
              disabled={newThreadButtonDisabled}
              data-testid="new-thread-button"
              aria-label="New conversation"
              onMouseOver={() => setIsTooltipOpen(true)}
              onMouseLeave={() => setIsTooltipOpen(false)}
              onFocus={() => setIsTooltipOpen(true)}
              onBlur={() => setIsTooltipOpen(false)}
            >
              <PlusIcon />
            </Button>
          </Tooltip>
        </div>

        {!isLoadingThreads && !!threads?.length && (
          <div className={styles.threadsWrapper}>
            <GroupedThreads
              groupedThreads={groupThreadsByDate(threads)}
              onDeleteThread={setThreadIdToDelete}
            />
            {/* Needs a height of >0px to be detected by intersectionObserver */}
            <div style={{ height: "1px" }} ref={lastElementRef} />
          </div>
        )}
        {isLoadingThreads && (
          <div className={styles.loadingState}>
            <Typography
              as="div"
              variant="heading_xs"
              className={styles.threadsSubHeading}
              data-testid="threads-group-heading"
              noMargin
            >
              Today
            </Typography>
            <Skeleton width="100%" height="2.5rem" />
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
